import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardStats, getWeeklyHours, getForecasting } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  Building2,
  MapPin,
  Clock,
  UserCheck,
  AlertTriangle,
  Palmtree,
  CalendarDays,
  TrendingUp,
  ArrowLeftRight
} from 'lucide-react';

export const DashboardPage = () => {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [weeklyHours, setWeeklyHours] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, hoursRes] = await Promise.all([
          getDashboardStats(),
          getWeeklyHours()
        ]);
        setStats(statsRes.data);
        setWeeklyHours(hoursRes.data);

        if (hasRole(['admin', 'manager'])) {
          const forecastRes = await getForecasting(2);
          setForecast(forecastRes.data.slice(0, 14));
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [hasRole]);

  const statCards = [
    { label: 'Total Employees', value: stats?.total_employees, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50', roles: ['admin', 'manager', 'supervisor'] },
    { label: 'Departments', value: stats?.total_departments, icon: Building2, color: 'text-violet-600', bgColor: 'bg-violet-50', roles: ['admin', 'manager'] },
    { label: 'Branches', value: stats?.total_branches, icon: MapPin, color: 'text-emerald-600', bgColor: 'bg-emerald-50', roles: ['admin', 'manager'] },
    { label: 'Clocked In Today', value: stats?.clocked_in_today, icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-50', roles: ['admin', 'manager', 'supervisor'] },
    { label: 'Currently Working', value: stats?.currently_working, icon: UserCheck, color: 'text-green-600', bgColor: 'bg-green-50', roles: ['admin', 'manager', 'supervisor'] },
    { label: 'Pending Vacations', value: stats?.pending_vacations, icon: Palmtree, color: 'text-cyan-600', bgColor: 'bg-cyan-50', roles: ['admin', 'manager', 'supervisor'] },
    { label: 'Pending Swaps', value: stats?.pending_swaps, icon: ArrowLeftRight, color: 'text-blue-600', bgColor: 'bg-blue-50', roles: ['admin', 'manager', 'supervisor', 'employee'] },
    { label: 'Flagged Entries', value: stats?.flagged_entries, icon: AlertTriangle, color: 'text-rose-600', bgColor: 'bg-rose-50', roles: ['admin', 'manager', 'supervisor'] },
    { label: 'Scheduled Today', value: stats?.scheduled_today, icon: CalendarDays, color: 'text-indigo-600', bgColor: 'bg-indigo-50', roles: ['admin', 'manager', 'supervisor'] },
  ];

  const filteredStatCards = statCards.filter(card => hasRole(card.roles));

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="dashboard-grid">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
          Welcome back, {user?.first_name}
        </h1>
        <p className="text-muted-foreground mt-1">
          Here's what's happening with your workforce today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-grid">
        {filteredStatCards.map((card, index) => (
          <Card 
            key={index} 
            className="stat-card"
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{card.label}</p>
                  <p className="text-3xl font-bold mt-2 font-['Manrope']">{card.value ?? 0}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${card.bgColor} flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weekly Hours Card - For all users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="weekly-hours-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Clock className="w-5 h-5" />
              This Week's Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold font-['JetBrains_Mono']">
                {weeklyHours?.total_hours?.toFixed(1) || '0.0'}
              </span>
              <span className="text-muted-foreground">hours</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              From {weeklyHours?.entries_count || 0} time entries
            </p>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-testid="quick-actions-card">
          <CardHeader>
            <CardTitle className="font-['Manrope']">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <a 
              href="/clock" 
              className="flex items-center gap-3 p-4 rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Clock className="w-5 h-5" />
              <span className="font-medium">Clock In/Out</span>
            </a>
            <a 
              href="/vacations" 
              className="flex items-center gap-3 p-4 rounded-xl bg-muted hover:bg-accent transition-colors"
            >
              <Palmtree className="w-5 h-5" />
              <span className="font-medium">Request Leave</span>
            </a>
            <a 
              href="/schedule" 
              className="flex items-center gap-3 p-4 rounded-xl bg-muted hover:bg-accent transition-colors"
            >
              <CalendarDays className="w-5 h-5" />
              <span className="font-medium">View Schedule</span>
            </a>
            {hasRole(['admin', 'manager', 'supervisor']) && (
              <a 
                href="/flagged" 
                className="flex items-center gap-3 p-4 rounded-xl bg-muted hover:bg-accent transition-colors"
              >
                <AlertTriangle className="w-5 h-5" />
                <span className="font-medium">Review Flagged</span>
              </a>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Forecasting - For managers */}
      {hasRole(['admin', 'manager']) && forecast.length > 0 && (
        <Card data-testid="forecasting-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <TrendingUp className="w-5 h-5" />
              Workforce Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {forecast.slice(0, 7).map((day, index) => (
                <div 
                  key={index}
                  className="p-3 rounded-xl bg-muted text-center"
                >
                  <p className="text-xs font-medium text-muted-foreground">
                    {day.day_name.slice(0, 3)}
                  </p>
                  <p className="text-lg font-bold mt-1 font-['JetBrains_Mono']">
                    {day.recommended_staff}
                  </p>
                  <p className="text-xs text-muted-foreground">staff</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Recommendations based on historical attendance patterns
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
